// src/components/VideoPlayerPanel.tsx
"use client";

import React, { useState, useRef, useEffect } from "react";
import { theme } from "@/styles/theme";
import type { ProjectDef } from "@/projectsConfig";

// ─── Source detection helpers ─────────────────────────────────────────────────

/** Extracts the 11-character video ID from common YouTube URL formats. */
function getYouTubeVideoId(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  return match ? match[1] : null;
}

/**
 * Extracts the file ID from a Google Drive share/view/preview URL.
 * Returns null for any non-Drive URL.
 *
 * Supported formats:
 *   https://drive.google.com/file/d/FILE_ID/view
 *   https://drive.google.com/file/d/FILE_ID/preview
 *   https://drive.google.com/open?id=FILE_ID
 *
 * NOTE: The file must be shared as "Anyone with the link can view".
 * Google Drive has no playback API, so the custom progress bar is not
 * available — only the native Google Drive player controls are shown.
 */
function getGoogleDriveFileId(url: string): string | null {
  // /file/d/{id}/view  or  /file/d/{id}/preview
  const pathMatch = url.match(/drive\.google\.com\/file\/d\/([^/?#]+)/);
  if (pathMatch) return pathMatch[1];
  // ?id={id}  (legacy open link)
  const paramMatch = url.match(/drive\.google\.com\/open\?.*[?&]id=([^&]+)/);
  if (paramMatch) return paramMatch[1];
  return null;
}

/** Minimal type shim for the YouTube IFrame API global. */
declare global {
  interface Window {
    YT: {
      Player: new (
        el: HTMLElement | string,
        options: {
          width?: string | number;
          height?: string | number;
          videoId: string;
          playerVars?: Record<string, number | string>;
          events?: {
            onReady?: (e: { target: YTPlayer }) => void;
          };
        }
      ) => YTPlayer;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YTPlayer {
  getCurrentTime(): number;
  getDuration(): number;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  destroy(): void;
}

// ─── Component ────────────────────────────────────────────────────────────────

type VideoPlayerPanelProps = {
  project: ProjectDef;
};

export function VideoPlayerPanel({ project }: VideoPlayerPanelProps) {
  const videoSrc = project.videoSrc ?? "";
  const youtubeId = videoSrc ? getYouTubeVideoId(videoSrc) : null;
  const driveId   = videoSrc ? getGoogleDriveFileId(videoSrc) : null;
  const isYouTube    = youtubeId !== null;
  const isGoogleDrive = driveId !== null;
  const isLocal      = !isYouTube && !isGoogleDrive;

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // Sync isFullscreen when browser exits fullscreen (e.g. Esc key)
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      try {
        await containerRef.current.requestFullscreen();
        // On mobile, attempt landscape lock (silently ignored if unsupported)
        const ori = screen.orientation as unknown as { lock?: (o: string) => Promise<void> };
        if (ori?.lock) ori.lock("landscape").catch(() => {});
      } catch { /* fullscreen denied or unsupported */ }
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };

  // ── Local video ─────────────────────────────────────────────────────────────
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!isLocal) return;
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => setCurrentTime(video.currentTime);
    const onLoadedMetadata = () => {
      setDuration(video.duration);
      video.play().catch(() => {/* autoplay blocked by browser */});
    };
    const onEnded = () => setCurrentTime(video.duration);

    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("ended", onEnded);

    // In case metadata is already available (e.g. cached)
    if (video.readyState >= 1) {
      setDuration(video.duration);
      video.play().catch(() => {});
    }

    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("ended", onEnded);
    };
  }, [isLocal]);

  const handleLocalSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const t = parseFloat(e.target.value);
    video.currentTime = t;
    setCurrentTime(t);
  };

  // ── YouTube IFrame API ───────────────────────────────────────────────────────
  const ytContainerRef = useRef<HTMLDivElement>(null);
  const ytPlayerRef = useRef<YTPlayer | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isYouTube || !youtubeId) return;

    let cancelled = false;

    const createPlayer = () => {
      if (cancelled || !ytContainerRef.current) return;

      ytPlayerRef.current = new window.YT.Player(ytContainerRef.current, {
        width: "100%",
        height: "100%",
        videoId: youtubeId,
        playerVars: {
          autoplay: 1,          // start playing immediately
          controls: 0,          // hide YouTube native controls
          disablekb: 1,         // disable keyboard shortcuts in the iframe
          modestbranding: 1,    // minimal YouTube branding
          rel: 0,               // no related videos at end
          iv_load_policy: 3,    // hide video annotations
          fs: 0,                // hide fullscreen button
          // Start timecode in seconds — set videoStartTime in projectsConfig.ts
          start: project.videoStartTime ?? 0,
        },
        events: {
          onReady: () => {
            // Poll player state every 250 ms to drive the custom progress bar
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = setInterval(() => {
              const p = ytPlayerRef.current;
              if (!p) return;
              try {
                const d = p.getDuration();
                const t = p.getCurrentTime();
                if (d > 0) setDuration(d);
                setCurrentTime(t);
              } catch {/* player not yet ready */}
            }, 250);
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      // API already loaded — create player immediately
      createPlayer();
    } else {
      // Inject the IFrame API script once; it calls onYouTubeIframeAPIReady when ready
      if (!document.getElementById("yt-iframe-api")) {
        const script = document.createElement("script");
        script.id = "yt-iframe-api";
        script.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(script);
      }
      window.onYouTubeIframeAPIReady = createPlayer;
    }

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      ytPlayerRef.current?.destroy();
      ytPlayerRef.current = null;
    };
  }, [isYouTube, youtubeId, project.videoStartTime]);

  const handleYouTubeSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = parseFloat(e.target.value);
    setCurrentTime(t);
    ytPlayerRef.current?.seekTo(t, true);
  };

  // ── Shared ───────────────────────────────────────────────────────────────────
  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        flexDirection: "column",
        background: theme.colors.DarkGray,
        color: theme.colors.White,
        fontFamily: theme.fonts.body,
        padding: "16px 20px",
        boxSizing: "border-box",
        gap: 16,
      }}
    >
      {/* Video area — 16:9 aspect ratio, no black bars */}
      <div
        style={{
          width: "100%",
          aspectRatio: "16 / 9",
          background: "#000",
          border: `1px solid ${theme.colors.White}`,
          boxSizing: "border-box",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {isYouTube ? (
          // The IFrame API replaces this div with a <iframe> on mount
          <div ref={ytContainerRef} style={{ width: "100%", height: "100%", position: "absolute", inset: 0 }} />
        ) : isGoogleDrive ? (
          // Google Drive preview embed — autoplay via URL param.
          // Native Drive controls are shown; no playback API available.
          <iframe
            src={`https://drive.google.com/file/d/${driveId}/preview?autoplay=1`}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
            allow="autoplay"
            allowFullScreen
          />
        ) : (
          <video
            ref={videoRef}
            src={project.videoSrc}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        )}
      </div>

      {/* Controls — slider hidden for Google Drive (no playback API), expand always visible */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {/* Slider — hidden for Google Drive */}
        {!isGoogleDrive && (
          <input
            type="range"
            min={0}
            max={duration || 0}
            value={currentTime}
            onChange={isYouTube ? handleYouTubeSliderChange : handleLocalSliderChange}
            style={{
              width: "100%",
              height: 20,
              appearance: "none",
              background: "transparent",
              outline: "none",
              cursor: "pointer",
            }}
            className="video-slider"
          />
        )}

        {/* Timecode row + fullscreen expand button */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            fontSize: 12,
            color: theme.colors.White,
            gap: 4,
          }}
        >
          {!isGoogleDrive && <span>{formatTime(currentTime)}</span>}
          {!isGoogleDrive && <span style={{ flex: 1, textAlign: "right" }}>{formatTime(duration)}</span>}
          {isGoogleDrive && <span style={{ flex: 1 }} />}
          <button
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            style={{
              background: "none",
              border: "none",
              color: theme.colors.White,
              cursor: "pointer",
              padding: "2px 4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              opacity: 0.8,
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = "1")}
            onMouseLeave={e => (e.currentTarget.style.opacity = "0.8")}
          >
            {isFullscreen ? (
              // Compress icon — arrows pointing inward
              <svg width="14" height="14" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4,1 4,4 1,4" />
                <polyline points="6,1 6,4 9,4" />
                <polyline points="1,6 4,6 4,9" />
                <polyline points="9,6 6,6 6,9" />
              </svg>
            ) : (
              // Expand icon — arrows pointing outward
              <svg width="14" height="14" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1,4 1,1 4,1" />
                <polyline points="6,1 9,1 9,4" />
                <polyline points="1,6 1,9 4,9" />
                <polyline points="9,6 9,9 6,9" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Custom slider styles — thin line track with Win95 thumb */}
      <style jsx>{`
        /* WebKit track: thin inset line */
        .video-slider::-webkit-slider-runnable-track {
          height: 2px;
          background: ${theme.colors.White};
          box-shadow: 0 -1px 0 0 #000, 0 1px 0 0 #808080;
        }

        /* WebKit thumb: Win95-style square button centred on the track */
        .video-slider::-webkit-slider-thumb {
          appearance: none;
          width: 10px;
          height: 16px;
          margin-top: -7px;
          background: ${theme.colors.Dark};
          border: 1px solid ${theme.colors.White};
          box-shadow: -1px -1px 0 0 rgba(127,127,127,0.5) inset,
                       1px  1px 0 0 ${theme.colors.LightGray} inset;
          cursor: pointer;
        }

        .video-slider::-webkit-slider-thumb:hover {
          border: 1px solid ${theme.colors.Green};
        }

        /* Firefox track */
        .video-slider::-moz-range-track {
          height: 2px;
          background: ${theme.colors.White};
          box-shadow: 0 -1px 0 0 #000, 0 1px 0 0 #808080;
          border: none;
        }

        /* Firefox thumb */
        .video-slider::-moz-range-thumb {
          width: 10px;
          height: 16px;
          background: ${theme.colors.Dark};
          border: 1px solid ${theme.colors.White};
          box-shadow: -1px -1px 0 0 rgba(127,127,127,0.5) inset,
                       1px  1px 0 0 ${theme.colors.LightGray} inset;
          cursor: pointer;
          border-radius: 0;
        }

        .video-slider::-moz-range-thumb:hover {
          border: 1px solid ${theme.colors.Green};
        }
      `}</style>
    </div>
  );
}
